/**
 * TNCY-6 §b — Access-grant panel on a project (owner tenant_admin only).
 *
 * Lists the project's `project_role_access_map` rows (GET …/access-grants)
 * with role, scope (project / contract), `source` (auto | manual) label,
 * status, and per-row controls: suspend/resume (PATCH), show-names toggle
 * (PATCH, XT-D9), and revoke (DELETE). Plus:
 *
 *   - the party pickers (§a) — Customer / Contractor / Reporting, each a
 *     friendly front-end over the explicit grant (POST …/access-grants);
 *   - an explicit-grant action for a NON-party tenant (e.g. an
 *     `ii_subcontractor`) — pick a known tenant + any role (XT-D23: the
 *     ii_subcontractor is NOT a party field, it needs a manual grant).
 *
 * Owner tenant_admin only: the panel renders for `tenant_admin` (the BE is
 * the security boundary — every write 403s for anyone else). The whole
 * surface is gated to OWN projects (a member project's grants are managed
 * by its owner).
 */
import { useMemo, useState, type ReactElement } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  FormControlLabel,
  IconButton,
  MenuItem,
  Paper,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { useToast } from "../../account/ToastContext.js";
import { ConfirmDialog } from "../../admin/shared/ConfirmDialog.js";
import type {
  AccessGrant,
  AccessRole,
  KnownTenant,
} from "../../tenancy/types.js";
import {
  useAccessGrantsQuery,
  useCreateAccessGrantMutation,
  useKnownTenantsQuery,
  useRevokeAccessGrantMutation,
  useUpdateAccessGrantMutation,
} from "./accessHooks.js";
import { PartyPicker } from "./PartyPicker.js";
import { ACCESS_ROLE_LABEL, accessRoleLabel } from "./roleLabels.js";
import { Trash2Icon } from "@aeriosiq/ui/icons";

export interface AccessGrantPanelProps {
  projectId: string;
  /** True when the project is the caller's OWN — grants are only managed by
   *  the owning tenant. Defaults true; the parent passes the row's ownership. */
  isOwnerProject?: boolean;
}

/** Roles offered in the "grant a non-party tenant" picker — manual grants
 *  the party pickers don't cover (chiefly `ii_subcontractor`, XT-D23). */
const MANUAL_GRANT_ROLES: readonly AccessRole[] = [
  "ii_subcontractor",
  "project_contractor",
  "reporting_subcontractor",
];

function statusChip(g: AccessGrant): ReactElement {
  if (g.status === "suspended") {
    return <Chip size="small" color="warning" label="Suspended" />;
  }
  if (g.status === "revoked") {
    return <Chip size="small" color="default" label="Revoked" />;
  }
  return <Chip size="small" color="success" label="Active" />;
}

export function AccessGrantPanel({
  projectId,
  isOwnerProject = true,
}: AccessGrantPanelProps): ReactElement {
  const { toast } = useToast();
  const grants = useAccessGrantsQuery(projectId, isOwnerProject);
  const known = useKnownTenantsQuery(isOwnerProject);
  const createGrant = useCreateAccessGrantMutation();
  const updateGrant = useUpdateAccessGrantMutation();
  const revokeGrant = useRevokeAccessGrantMutation();

  const [revokeTarget, setRevokeTarget] = useState<AccessGrant | null>(null);
  const [manualTenant, setManualTenant] = useState<string>("");
  const [manualRole, setManualRole] = useState<AccessRole>("ii_subcontractor");

  const items = useMemo(() => grants.data?.items ?? [], [grants.data]);
  const knownTenants: KnownTenant[] = known.data?.items ?? [];

  // Tenant ids already holding a given role (for the party-picker "already
  // shared" hint). Only active/suspended grants count — a revoked grant can
  // be re-granted.
  const grantedByRole = useMemo(() => {
    const map = new Map<AccessRole, Set<string>>();
    for (const g of items) {
      if (g.status === "revoked") continue;
      const set = map.get(g.role) ?? new Set<string>();
      set.add(g.tenantId);
      map.set(g.role, set);
    }
    return map;
  }, [items]);

  function grantedSet(role: AccessRole): ReadonlySet<string> {
    return grantedByRole.get(role) ?? new Set<string>();
  }

  function shareWithTenant(args: { tenantId: string; role: AccessRole }): void {
    const tenantName =
      knownTenants.find((t) => t.tenantId === args.tenantId)?.tenantName ??
      "the company";
    createGrant.mutate(
      {
        projectId,
        body: {
          tenantId: args.tenantId,
          role: args.role,
          showUserNames: false,
        },
      },
      {
        onSuccess: () => {
          toast(
            `Shared with ${tenantName} as ${accessRoleLabel(args.role)}.`,
            "success",
          );
        },
        onError: () => {
          toast(
            `Couldn't share with ${tenantName} — they may not be linked, or already have that role.`,
            "error",
          );
        },
      },
    );
  }

  function toggleSuspend(g: AccessGrant): void {
    const suspend = g.status === "active";
    updateGrant.mutate(
      { projectId, tenantId: g.tenantId, body: { suspended: suspend } },
      {
        onError: () => {
          toast("Couldn't update the grant — try again.", "error");
        },
      },
    );
  }

  function toggleShowNames(g: AccessGrant, next: boolean): void {
    updateGrant.mutate(
      { projectId, tenantId: g.tenantId, body: { showUserNames: next } },
      {
        onError: () => {
          toast("Couldn't update show-names — try again.", "error");
        },
      },
    );
  }

  function confirmRevoke(): void {
    if (!revokeTarget) return;
    const g = revokeTarget;
    revokeGrant.mutate(
      { projectId, tenantId: g.tenantId },
      {
        onSuccess: () => {
          toast(`Revoked ${g.tenantName}'s access.`, "success");
        },
        onError: () => {
          toast("Couldn't revoke the grant — try again.", "error");
        },
        onSettled: () => {
          setRevokeTarget(null);
        },
      },
    );
  }

  function grantManual(): void {
    if (!manualTenant) return;
    shareWithTenant({ tenantId: manualTenant, role: manualRole });
    setManualTenant("");
  }

  if (!isOwnerProject) {
    return (
      <Alert severity="info" variant="outlined">
        Access for a shared project is managed by its owning company.
      </Alert>
    );
  }

  return (
    <Stack spacing={3}>
      {/* ── Party pickers ─────────────────────────────────────────── */}
      <Paper variant="outlined" sx={{ p: 2 }}>
        <Typography variant="h6" gutterBottom>
          Project parties
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Sharing a project with a partner company grants them the matching
          role. You can only share with companies you&apos;re already linked to.
        </Typography>
        <Stack spacing={2} divider={<Divider flexItem />}>
          {(["customer", "contractor", "reporting"] as const).map((kind) => {
            const role: AccessRole =
              kind === "customer"
                ? "project_customer"
                : kind === "contractor"
                  ? "project_contractor"
                  : "reporting_subcontractor";
            // FB-03 — the reporting picker's "Our team" branch offers to
            // revoke the active reporting grant (if any). Reporting is a
            // single-party slot, so the first non-revoked reporting grant is it.
            const reportingGrant =
              kind === "reporting"
                ? (items.find(
                    (g) =>
                      g.role === "reporting_subcontractor" &&
                      g.status !== "revoked",
                  ) ?? null)
                : null;
            return (
              <PartyPicker
                key={kind}
                kind={kind}
                knownTenants={knownTenants}
                knownTenantsLoading={known.isPending}
                alreadyGrantedTenantIds={grantedSet(role)}
                onShareWithTenant={shareWithTenant}
                {...(kind === "reporting"
                  ? {
                      existingGrant: reportingGrant,
                      onRevoke: (g: AccessGrant) => {
                        setRevokeTarget(g);
                      },
                    }
                  : {})}
                pending={createGrant.isPending}
              />
            );
          })}
        </Stack>
      </Paper>

      {/* ── Grants list ───────────────────────────────────────────── */}
      <Paper variant="outlined" sx={{ p: 2 }}>
        <Typography variant="h6" gutterBottom>
          Access grants
        </Typography>

        {grants.isPending ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
            <CircularProgress />
          </Box>
        ) : grants.isError ? (
          <Alert severity="error">Couldn&apos;t load access grants.</Alert>
        ) : items.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            No companies have access to this project yet.
          </Typography>
        ) : (
          <Stack
            component="ul"
            spacing={1}
            sx={{ listStyle: "none", p: 0, m: 0 }}
            aria-label="Access grants"
          >
            {items.map((g) => (
              <Box
                component="li"
                key={g.accessId}
                sx={{
                  display: "flex",
                  flexWrap: "wrap",
                  alignItems: "center",
                  gap: 1.5,
                  p: 1.5,
                  border: 1,
                  borderColor: "divider",
                  borderRadius: 1,
                }}
              >
                <Box sx={{ flex: "1 1 200px", minWidth: 180 }}>
                  <Typography variant="subtitle2">{g.tenantName}</Typography>
                  <Stack direction="row" spacing={0.5} alignItems="center">
                    <Chip
                      size="small"
                      variant="outlined"
                      label={accessRoleLabel(g.role)}
                    />
                    <Chip
                      size="small"
                      variant="outlined"
                      label={g.contractId ? "Contract" : "Project"}
                    />
                    <Tooltip
                      title={
                        g.source === "auto"
                          ? "Created automatically from a party field"
                          : "Granted manually"
                      }
                    >
                      <Chip
                        size="small"
                        variant="outlined"
                        label={g.source === "auto" ? "Auto" : "Manual"}
                      />
                    </Tooltip>
                  </Stack>
                </Box>

                {statusChip(g)}

                <FormControlLabel
                  control={
                    <Switch
                      size="small"
                      checked={g.showUserNames}
                      onChange={(_e, c) => {
                        toggleShowNames(g, c);
                      }}
                      inputProps={{
                        "aria-label": `Show user names for ${g.tenantName}`,
                      }}
                    />
                  }
                  label="Show names"
                  sx={{ mr: 0 }}
                />

                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => {
                    toggleSuspend(g);
                  }}
                  disabled={g.status === "revoked"}
                >
                  {g.status === "suspended" ? "Resume" : "Suspend"}
                </Button>

                <Tooltip title="Revoke access">
                  <span>
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => {
                        setRevokeTarget(g);
                      }}
                      disabled={g.status === "revoked"}
                      aria-label={`Revoke ${g.tenantName}'s access`}
                    >
                      <Trash2Icon fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
              </Box>
            ))}
          </Stack>
        )}

        {/* ── Explicit grant for a non-party tenant ───────────────── */}
        <Divider sx={{ my: 2 }} />
        <Typography variant="subtitle2" gutterBottom>
          Grant another company (e.g. an on-site subcontractor)
        </Typography>
        {!known.isPending && knownTenants.length === 0 ? (
          <Alert severity="warning" variant="outlined">
            No linked companies to grant. Invite them first on Partner
            Companies.
          </Alert>
        ) : (
          <Stack
            direction="row"
            spacing={1}
            alignItems="flex-start"
            useFlexGap
            // FB-24 — the two min-200 selects + the Grant button can't shrink
            // below their content; without wrap they push a few-px (and at
            // narrow widths a large) horizontal scrollbar on the wizard's
            // scroll container. `flexWrap` lets the button drop to a new line
            // instead of overflowing. `minWidth:0` lets the selects share the
            // track at intermediate widths before they wrap.
            sx={{ flexWrap: "wrap" }}
          >
            <TextField
              select
              size="small"
              label="Company"
              value={manualTenant}
              onChange={(e) => {
                setManualTenant(e.target.value);
              }}
              sx={{ minWidth: 200, flex: "1 1 200px" }}
              aria-label="Company to grant"
            >
              {knownTenants.map((t) => (
                <MenuItem key={t.tenantId} value={t.tenantId}>
                  {t.tenantName}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              select
              size="small"
              label="Role"
              value={manualRole}
              onChange={(e) => {
                setManualRole(e.target.value as AccessRole);
              }}
              sx={{ minWidth: 200, flex: "1 1 200px" }}
              aria-label="Role to grant"
            >
              {MANUAL_GRANT_ROLES.map((r) => (
                <MenuItem key={r} value={r}>
                  {ACCESS_ROLE_LABEL[r]}
                </MenuItem>
              ))}
            </TextField>
            <Button
              variant="contained"
              onClick={grantManual}
              disabled={manualTenant === "" || createGrant.isPending}
            >
              Grant
            </Button>
          </Stack>
        )}
      </Paper>

      <ConfirmDialog
        open={revokeTarget !== null}
        title="Revoke access?"
        description={
          revokeTarget
            ? `${revokeTarget.tenantName} will lose ${accessRoleLabel(
                revokeTarget.role,
              )} access to this project. Existing cross-tenant assignments stay intact; this only removes the grant.`
            : ""
        }
        confirmLabel="Revoke"
        danger
        pending={revokeGrant.isPending}
        onConfirm={confirmRevoke}
        onCancel={() => {
          setRevokeTarget(null);
        }}
      />
    </Stack>
  );
}
